import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  BulkCreateTimeSeriesDataPointsDto,
  CreateTimeSeriesDataPointDto,
  PagedTimeSeriesDataPointsDto,
  TimeSeriesDataPointDto,
  TimeSeriesTimestampUsageDto,
  UpdateTimeSeriesDataPointDto,
} from '@anessa/shared-models';
import {
  PagedTimeSeriesDataPoints,
  TimeSeriesDataPoint,
  TimeSeriesTimestampUsage,
} from '../models/timeseries-data-point.types';

@Injectable({ providedIn: 'root' })
export class TimeSeriesDataPointsService {
  private readonly http = inject(HttpClient);

  list(
    tableId: string,
    options?: { dateFrom?: Date; dateTo?: Date },
  ): Observable<TimeSeriesDataPoint[]> {
    let params = new HttpParams();
    if (options?.dateFrom)
      params = params.set('dateFrom', options.dateFrom.toISOString());
    if (options?.dateTo)
      params = params.set('dateTo', options.dateTo.toISOString());
    return this.http.get<TimeSeriesDataPointDto[]>(
      `/api/timeseries-data/${tableId}`,
      { params },
    );
  }

  listPaged(
    tableId: string,
    page: number,
    limit: number,
    options?: { dateFrom?: Date; dateTo?: Date },
  ): Observable<PagedTimeSeriesDataPoints> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));
    if (options?.dateFrom)
      params = params.set('dateFrom', options.dateFrom.toISOString());
    if (options?.dateTo)
      params = params.set('dateTo', options.dateTo.toISOString());
    return this.http.get<PagedTimeSeriesDataPointsDto>(
      `/api/timeseries-data/${tableId}/paged`,
      { params },
    );
  }

  isTimestampInUse(
    tableId: string,
    timestamp: string,
    excludePointId?: string,
  ): Observable<TimeSeriesTimestampUsage> {
    let params = new HttpParams().set('timestamp', timestamp);
    if (excludePointId) {
      params = params.set('excludePointId', excludePointId);
    }

    return this.http.get<TimeSeriesTimestampUsageDto>(
      `/api/timeseries-data/${tableId}/timestamp-in-use`,
      { params },
    );
  }

  create(
    tableId: string,
    dto: CreateTimeSeriesDataPointDto,
  ): Observable<TimeSeriesDataPoint> {
    return this.http.post<TimeSeriesDataPointDto>(
      `/api/timeseries-data/${tableId}`,
      dto,
    );
  }

  bulkCreate(
    tableId: string,
    dto: BulkCreateTimeSeriesDataPointsDto,
  ): Observable<TimeSeriesDataPoint[]> {
    return this.http.post<TimeSeriesDataPointDto[]>(
      `/api/timeseries-data/${tableId}/bulk`,
      dto,
    );
  }

  update(
    tableId: string,
    pointId: string,
    dto: UpdateTimeSeriesDataPointDto,
  ): Observable<TimeSeriesDataPoint> {
    return this.http.patch<TimeSeriesDataPointDto>(
      `/api/timeseries-data/${tableId}/${pointId}`,
      dto,
    );
  }

  deletePoint(
    tableId: string,
    pointId: string,
  ): Observable<{ deletedCount: number }> {
    return this.http.delete<{ deletedCount: number }>(
      `/api/timeseries-data/${tableId}/${pointId}`,
    );
  }

  deleteAllForTable(tableId: string): Observable<{ deletedCount: number }> {
    return this.http.delete<{ deletedCount: number }>(
      `/api/timeseries-data/${tableId}/all`,
    );
  }
}
